import { useReducer, useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useLocation } from 'react-router'
import { Box, Button, Input, PasswordInput, Stack, Text } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { AuthLayout } from '@/features/auth/components/AuthLayout'
import { ErrorMessage } from '@/shared/components/ErrorMessage'
import { Logo } from '@/shared/components/Logo'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { ApiError } from '@/shared/api/errors'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { isIdleLogoutState } from '@/features/auth/model/idleLogoutState'
import { MfaForm } from '@/features/auth/components/MfaForm'
import styles from './LoginPage.module.css'

type State = { screen: 'LOGIN' } | { screen: 'SMS'; mfaToken: string }

type Action =
  | { type: 'MFA_REQUIRED'; mfaToken: string }
  | { type: 'AUTHENTICATED' }
  | { type: 'BACK_TO_LOGIN' }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'MFA_REQUIRED':
      return { screen: 'SMS', mfaToken: action.mfaToken }
    case 'AUTHENTICATED':
    case 'BACK_TO_LOGIN':
      return { screen: 'LOGIN' }
    default:
      return state
  }
}

const initialState: State = { screen: 'LOGIN' }

const LoginPage = () => {
  const { t } = useTranslation()
  useDocumentTitle(t('titles.login'))
  const { login } = useAuth()
  const location = useLocation()
  const loggedOutDueToIdle = isIdleLogoutState(location.state)

  const messageForLoginError = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 401) return t('auth.errors.invalidCredentials')
      if (err.status === 400) return t('auth.errors.badRequest')
      if (err.status >= 500) return t('auth.errors.server')
      return t('auth.errors.generic')
    }
    return t('auth.errors.network')
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const submit = useMutation({
    mutationFn: () => login(username, password),
    onSuccess: result => {
      if (result.kind === 'mfa_required') {
        dispatch({ type: 'MFA_REQUIRED', mfaToken: result.mfaToken })
      } else {
        dispatch({ type: 'AUTHENTICATED' })
      }
    },
  })

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    submit.mutate()
  }

  if (state.screen === 'SMS') {
    return (
      <AuthLayout>
        <MfaForm
          mfaToken={state.mfaToken}
          onSuccess={() => dispatch({ type: 'AUTHENTICATED' })}
          onAbort={() => dispatch({ type: 'BACK_TO_LOGIN' })}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} noValidate>
        <Stack direction='vertical' space='16'>
          <Logo className={styles.logo} />

          {loggedOutDueToIdle && (
            <Box
              id='idle-logout-notice'
              role='status'
              padding='12'
              borderRadius='8'
              backgroundColor='wheat-alternate'
            >
              <Text variant='small'>{t('auth.idle.loggedOutMessage')}</Text>
            </Box>
          )}

          <Input
            id='email'
            name='username'
            type='email'
            autoComplete='email'
            required
            label={t('auth.email')}
            value={username}
            onChange={e => setUsername(e.currentTarget.value)}
            disabled={submit.isPending}
          />

          <PasswordInput
            id='password'
            name='password'
            autoComplete='current-password'
            required
            label={t('auth.password')}
            value={password}
            onChange={e => setPassword(e.currentTarget.value)}
            disabled={submit.isPending}
          />

          {submit.isError && (
            <ErrorMessage id='login-errors'>
              {messageForLoginError(submit.error)}
            </ErrorMessage>
          )}

          <Button
            type='submit'
            variant='primary'
            color='teal'
            loading={submit.isPending}
            disabled={!username || !password}
          >
            {t('auth.login')}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  )
}

export default LoginPage
